/**
 * Clipboard helpers for copying/pasting images in the renderer (Electron + web).
 */

/**
 * Exported so lib/images/image-resize.js can reuse it rather than carry a second FileReader wrapper.
 * @param {Blob} blob
 * @returns {Promise<string>}
 */
export function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error || new Error("Failed to read image"));
    reader.readAsDataURL(blob);
  });
}

/**
 * Convert any image blob to PNG — ClipboardItem commonly accepts image/png most reliably.
 * @param {Blob} blob
 * @returns {Promise<Blob>}
 */
export async function blobToPngBlob(blob) {
  if (!blob) {
    throw new Error("No image blob");
  }
  if (blob.type === "image/png") {
    return blob;
  }
  const bitmap = await createImageBitmap(blob);
  try {
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      throw new Error("Canvas unavailable");
    }
    ctx.drawImage(bitmap, 0, 0);
    const png = await new Promise((resolve, reject) => {
      canvas.toBlob((next) => (next ? resolve(next) : reject(new Error("PNG encode failed"))), "image/png");
    });
    return png;
  } finally {
    bitmap.close?.();
  }
}

/**
 * Write an image blob to the system clipboard.
 * Prefers Electron's native clipboard (reliable for PNG paste into chat / other apps).
 * @param {Blob} blob
 * @returns {Promise<void>}
 */
export async function copyImageBlobToClipboard(blob) {
  const png = await blobToPngBlob(blob);

  const electronWrite = window.electronAPI?.writeClipboardImage;
  if (typeof electronWrite === "function") {
    const dataUrl = await blobToDataUrl(png);
    await electronWrite(dataUrl);
    return;
  }

  if (!navigator.clipboard?.write || typeof ClipboardItem === "undefined") {
    throw new Error("Image clipboard is not supported here");
  }
  // Chromium expects a Promise-valued ClipboardItem entry in several versions.
  await navigator.clipboard.write([new ClipboardItem({ "image/png": Promise.resolve(png) })]);
}

/**
 * Fetch an object/HTTP URL and copy it as an image.
 * @param {string} url
 * @returns {Promise<void>}
 */
export async function copyImageFromUrl(url) {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error("Failed to load image");
  }
  const next = await res.blob();
  await copyImageBlobToClipboard(next);
}

/**
 * Collect image files from a paste (or drop) ClipboardData / DataTransfer.
 * Prefers `items` (screenshots) and falls back to `files`.
 * @param {DataTransfer | null | undefined} data
 * @returns {File[]}
 */
export function imageFilesFromClipboardData(data) {
  if (!data) return [];
  /** @type {File[]} */
  const out = [];
  const items = data.items;
  if (items?.length) {
    for (let i = 0; i < items.length; i += 1) {
      const item = items[i];
      if (item.kind !== "file" || !String(item.type || "").startsWith("image/")) {
        continue;
      }
      const file = item.getAsFile();
      if (file) {
        // Screenshots often arrive as "image.png" with no useful name — stamp a timestamp.
        if (!file.name || file.name === "image.png") {
          const ext = (file.type.split("/")[1] || "png").replace("jpeg", "jpg");
          out.push(new File([file], `pasted-image-${Date.now()}-${i}.${ext}`, { type: file.type }));
        } else {
          out.push(file);
        }
      }
    }
  }
  if (out.length) return out;
  return Array.from(data.files || []).filter((f) => String(f.type || "").startsWith("image/"));
}
